/**
 * EXP-01F — Deterministic Contextual Ask Prashasta
 *
 * Ranking algorithm: event metadata + journey state → ordered Q&A list.
 * No LLM. No free-text generation. 100% curated answers.
 *
 * Single access point for journey state — do NOT read localStorage directly
 * elsewhere on the Event Story page.
 */
import { loadJourneyFromStorage } from '../journey/journeyEngine';

const INITIAL_VISIBLE = 5;

// ── Journey context ────────────────────────────────────────────────────────────

/**
 * Read and normalise the canonical prashasta_event_journey state.
 * Returns null when no session is active (direct URL visitor).
 */
export function getJourneyContext() {
  const s = loadJourneyFromStorage();
  if (!s) return null;
  return {
    eventType:     s.event_type     || null,
    styleTags:     (s.style_tags    || []).map(t => (t || '').toUpperCase()),
    guestRange:    s.guest_range    || null,
    venueSetting:  s.venue_setting  || null,
    planningScope: s.planning_scope || null,
    selectedSlug:  s.selected_inspiration?.slug || null,
  };
}

// ── Scoring ────────────────────────────────────────────────────────────────────

function scoreItem(item, event, journey) {
  let score = item.priority ?? 50;

  // ── Event-type relevance ───────────────────────────────────────────────────
  const appTypes = item.applicable_event_types || [];
  if (appTypes.length === 0) {
    score += 5;               // generic fallback — visible when nothing else matches
  } else if (appTypes.includes(event.event_type)) {
    score += 15;
  } else {
    score -= 40;              // heavy penalty for cross-type items — should never surface
  }

  // ── Style-tag relevance (event's own style_tags) ───────────────────────────
  const eventStyles = (event.style_tags || []).map(t => (t || '').toUpperCase());
  const appStyles   = (item.applicable_styles || []).map(t => (t || '').toUpperCase());
  const styleMatchCount = appStyles.filter(s => eventStyles.includes(s)).length;
  score += styleMatchCount * 8;

  // ── Venue-setting relevance (event's own venue_setting) ────────────────────
  const appSettings = item.applicable_settings || [];
  if (appSettings.length > 0) {
    const evSetting = event.venue_setting || '';
    if (
      appSettings.includes(evSetting) ||
      appSettings.includes('BOTH') ||
      evSetting === 'BOTH'
    ) {
      score += 5;
    }
  }

  // ── Guest-scale relevance (event's own guest_range) ────────────────────────
  const appScales = item.applicable_guest_scales || [];
  if (appScales.length > 0 && appScales.includes(event.guest_range)) {
    score += 5;
  }

  if (!journey) return score;

  // ── Journey signal boosts ──────────────────────────────────────────────────
  // Applied when the user's stated journey preference matches the item's signals.
  for (const signal of (item.journey_signals || [])) {
    switch (signal) {
      case 'venue_setting':
        if (
          journey.venueSetting &&
          (item.applicable_settings || []).includes(journey.venueSetting)
        ) score += 20;
        break;

      case 'guest_range':
        if (
          journey.guestRange &&
          (item.applicable_guest_scales || []).includes(journey.guestRange)
        ) score += 20;
        break;

      case 'planning_scope':
        if (
          journey.planningScope &&
          (item.applicable_planning_scopes || []).includes(journey.planningScope)
        ) score += 20;
        break;

      case 'style': {
        const journeyStyles = journey.styleTags || [];
        const overlap = appStyles.filter(s => journeyStyles.includes(s)).length;
        score += overlap * 15;
        break;
      }

      case 'event_type':
        if (
          journey.eventType &&
          (item.applicable_event_types || []).includes(journey.eventType)
        ) score += 10;
        break;

      default:
        break;
    }
  }

  return score;
}

// ── Public ranking function ────────────────────────────────────────────────────

/**
 * Rank Q&A items for the given event + journey context.
 *
 * @param {Array}  qaItems      Full contextual_qa array from the event.
 * @param {Object} event        Public event object (event_type, style_tags, etc.)
 * @param {Object|null} journey Normalised journey context from getJourneyContext().
 *
 * @returns {{ primary: Array, secondary: Array, ctaItem: Object|null }}
 *   primary:   top INITIAL_VISIBLE non-CTA items — always shown
 *   secondary: remaining non-CTA items — hidden behind "Show more"
 *   ctaItem:   highest-priority CTA item — shown as fallback at bottom
 */
export function rankQAForContext(qaItems, event, journey) {
  if (!qaItems?.length || !event) {
    return { primary: [], secondary: [], ctaItem: null };
  }

  const ctaItems     = qaItems.filter(item => item.category === 'cta');
  const contentItems = qaItems.filter(
    item => item.category !== 'cta' && item.status !== 'DRAFT'
  );

  const scored = contentItems
    .map(item => ({ ...item, _score: scoreItem(item, event, journey) }))
    .sort((a, b) => b._score - a._score);

  const primary   = scored.slice(0, INITIAL_VISIBLE);
  const secondary = scored.slice(INITIAL_VISIBLE);

  // Best CTA item (highest base priority wins)
  const ctaItem = ctaItems.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0] ?? null;

  return { primary, secondary, ctaItem };
}

export { INITIAL_VISIBLE };
